import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import UnvModal from 'components/UnvDialog/UnvModal'
import { enrollStudent } from 'service/StudentsService'
import styles from '../AdminFullPage.module.scss'

const initialForm = {
  surname: '',
  first_name: '',
  last_name: '',
  birth_date: '',
  gender: '',
  inn: '',
  passport: '',
  phone: '',
  email: '',
  address: '',
  faculty: '',
  direction: '',
  group: '',
  course: 1,
  form: 'full_time',
  funding: 'budget',
  language: 'ru',
  year_of_admission: new Date().getFullYear(),
}

function Step({ index, label, current }) {
  const cls =
    index === current
      ? `${styles.step} ${styles.step_active}`
      : index < current
        ? `${styles.step} ${styles.step_done}`
        : styles.step
  return <div className={cls}>{`${index + 1}. ${label}`}</div>
}

function EnrollTab({ notify }) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(0)
  const [form, setForm] = useState(initialForm)
  const [done, setDone] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setStep(0)
    setForm(initialForm)
    setDone(null)
  }

  const onChange = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e.target.value }))

  const next = () => setStep((s) => Math.min(3, s + 1))
  const prev = () => setStep((s) => Math.max(0, s - 1))

  const submit = () => {
    setSubmitting(true)
    enrollStudent(form)
      .then((res) => {
        const data = res?.data || res
        setDone({
          login: data?.username || data?.inn || form.inn,
          password: data?.generated_password,
          studentId: data?.id,
        })
        notify(t('admin.fullPage.enroll_success'), 'success')
      })
      .catch((err) => {
        notify(err?.response?.data?.detail || t('admin.fullPage.enroll_error'), 'error')
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <div>
      <h2 className={styles.section_title}>{t('admin.fullPage.enroll_title')}</h2>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16 }}>
        {t('admin.fullPage.enroll_intro')}
      </p>
      <button
        type="button"
        className="unv-btn"
        onClick={() => {
          reset()
          setOpen(true)
        }}
      >
        {t('admin.fullPage.enroll_btn')}
      </button>

      <UnvModal
        open={open}
        title={t('admin.fullPage.enroll_modal_title')}
        onClose={() => setOpen(false)}
        width={720}
        footer={
          done ? (
            <button
              type="button"
              className="unv-btn"
              onClick={() => setOpen(false)}
            >
              {t('admin.fullPage.enroll_done')}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="unv-btn unv-btn--ghost"
                onClick={prev}
                disabled={step === 0}
              >
                {t('common.back')}
              </button>
              {step < 3 ? (
                <button type="button" className="unv-btn" onClick={next}>
                  {t('admin.fullPage.enroll_next')}
                </button>
              ) : (
                <button
                  type="button"
                  className="unv-btn"
                  onClick={submit}
                  disabled={submitting}
                >
                  {submitting ? t('admin.fullPage.enroll_sending') : t('admin.fullPage.enroll_accept')}
                </button>
              )}
            </>
          )
        }
      >
        {done ? (
          <div>
            <p>{t('admin.fullPage.enroll_created_save')}</p>
            <div className={styles.password_box}>
              {t('admin.fullPage.enroll_login_label')}: {done.login}
              {done.password ? (
                <>
                  <br />
                  {t('admin.fullPage.enroll_password_label')}: {done.password}
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <div className={styles.steps}>
              <Step index={0} label={t('admin.fullPage.enroll_step_personal')} current={step} />
              <Step index={1} label={t('admin.fullPage.enroll_step_contacts')} current={step} />
              <Step index={2} label={t('admin.fullPage.enroll_step_studies')} current={step} />
              <Step index={3} label={t('admin.fullPage.enroll_step_confirm')} current={step} />
            </div>

            {step === 0 ? (
              <>
                <div className={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_surname')}</label>
                    <input
                      className="unv-input"
                      value={form.surname}
                      onChange={onChange('surname')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_first_name')}</label>
                    <input
                      className="unv-input"
                      value={form.first_name}
                      onChange={onChange('first_name')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_last_name')}</label>
                    <input
                      className="unv-input"
                      value={form.last_name}
                      onChange={onChange('last_name')}
                    />
                  </div>
                </div>
                <div className={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_birth_date')}</label>
                    <input
                      className="unv-input"
                      type="date"
                      value={form.birth_date}
                      onChange={onChange('birth_date')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_gender')}</label>
                    <select
                      className="unv-select"
                      value={form.gender}
                      onChange={onChange('gender')}
                    >
                      <option value="">—</option>
                      <option value="male">{t('admin.fullPage.gender_male')}</option>
                      <option value="female">{t('admin.fullPage.gender_female')}</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.kv_inn')}</label>
                    <input
                      className="unv-input"
                      value={form.inn}
                      onChange={onChange('inn')}
                    />
                  </div>
                </div>
                <div className={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_passport')}</label>
                    <input
                      className="unv-input"
                      value={form.passport}
                      onChange={onChange('passport')}
                    />
                  </div>
                </div>
              </>
            ) : null}

            {step === 1 ? (
              <>
                <div className={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('common.phone')}</label>
                    <input
                      className="unv-input"
                      value={form.phone}
                      onChange={onChange('phone')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">Email</label>
                    <input
                      className="unv-input"
                      type="email"
                      value={form.email}
                      onChange={onChange('email')}
                    />
                  </div>
                </div>
                <div>
                  <label className="unv-label">{t('common.address')}</label>
                  <textarea
                    className="unv-textarea"
                    value={form.address}
                    onChange={onChange('address')}
                  />
                </div>
              </>
            ) : null}

            {step === 2 ? (
              <>
                <div className={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_faculty')}</label>
                    <input
                      className="unv-input"
                      value={form.faculty}
                      onChange={onChange('faculty')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_direction')}</label>
                    <input
                      className="unv-input"
                      value={form.direction}
                      onChange={onChange('direction')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_group')}</label>
                    <input
                      className="unv-input"
                      value={form.group}
                      onChange={onChange('group')}
                    />
                  </div>
                </div>
                <div className={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_course')}</label>
                    <select
                      className="unv-select"
                      value={form.course}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          course: Number(e.target.value),
                        }))
                      }
                    >
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                      <option value={4}>4</option>
                      <option value={5}>5</option>
                      <option value={6}>6</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_form')}</label>
                    <select
                      className="unv-select"
                      value={form.form}
                      onChange={onChange('form')}
                    >
                      <option value="full_time">{t('admin.fullPage.form_full_time')}</option>
                      <option value="part_time">{t('admin.fullPage.form_part_time')}</option>
                      <option value="evening">{t('admin.fullPage.form_evening')}</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_funding')}</label>
                    <select
                      className="unv-select"
                      value={form.funding}
                      onChange={onChange('funding')}
                    >
                      <option value="budget">{t('admin.fullPage.funding_budget')}</option>
                      <option value="contract">{t('admin.fullPage.funding_contract')}</option>
                    </select>
                  </div>
                </div>
                <div className={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_language')}</label>
                    <select
                      className="unv-select"
                      value={form.language}
                      onChange={onChange('language')}
                    >
                      <option value="ru">{t('admin.fullPage.lang_ru')}</option>
                      <option value="ky">{t('admin.fullPage.lang_ky')}</option>
                      <option value="en">{t('admin.fullPage.lang_en')}</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="unv-label">{t('admin.fullPage.field_year')}</label>
                    <input
                      className="unv-input"
                      type="number"
                      value={form.year_of_admission}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          year_of_admission: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
              </>
            ) : null}

            {step === 3 ? (
              <div className={styles.kv}>
                <div className={styles.kv_key}>{t('common.fullName')}</div>
                <div>
                  {form.surname} {form.first_name} {form.last_name}
                </div>
                <div className={styles.kv_key}>{t('admin.fullPage.field_birth_date')}</div>
                <div>{form.birth_date || '—'}</div>
                <div className={styles.kv_key}>{t('admin.fullPage.kv_inn')}</div>
                <div>{form.inn || '—'}</div>
                <div className={styles.kv_key}>{t('admin.fullPage.enroll_kv_email_phone')}</div>
                <div>
                  {form.email || '—'} / {form.phone || '—'}
                </div>
                <div className={styles.kv_key}>{t('admin.fullPage.field_faculty')}</div>
                <div>{form.faculty || '—'}</div>
                <div className={styles.kv_key}>{t('admin.fullPage.field_direction')}</div>
                <div>{form.direction || '—'}</div>
                <div className={styles.kv_key}>{t('admin.fullPage.enroll_kv_group_course')}</div>
                <div>
                  {form.group || '—'} / {form.course}
                </div>
                <div className={styles.kv_key}>{t('admin.fullPage.enroll_kv_form_funding')}</div>
                <div>
                  {form.form} / {form.funding}
                </div>
                <div className={styles.kv_key}>{t('admin.fullPage.enroll_kv_language_year')}</div>
                <div>
                  {form.language} / {form.year_of_admission}
                </div>
              </div>
            ) : null}
          </>
        )}
      </UnvModal>
    </div>
  )
}

export default EnrollTab
